import React, { useEffect, useCallback } from 'react';
import { render } from 'react-dom';
import Icon from '@ant-design/icons';

import { ReactComponent as SuccessIcon } from '@svg/done.svg';

import { ToastContainer, MultipleLineContainer, MultipleLineContent } from './style';

export interface ToastProps {
    content?: React.ReactChild;
    duration?: number;
    callback?: () => void;
    container: Node;
    wrapperClassName?: string;
    multipleLineMode?: boolean;
}

export type ToastConfig = Omit<ToastProps, 'container'>;

const Toast: React.FC<ToastProps> = ({
    content,
    duration = 3,
    container,
    callback,
    wrapperClassName,
    multipleLineMode
}) => {
    useEffect(() => {
        const timer = setTimeout(() => {
            document.body.removeChild(container);
            callback && callback();
        }, duration * 1000);
        return () => clearTimeout(timer);
    }, [duration, container, callback]);

    const multiRef = useCallback((node: HTMLDivElement) => {
        if (node) {
            const winW = window.document.body.clientWidth;
            const curW = node.clientWidth;
            if (winW !== curW) {
                node.style.transform = `translateX(${(winW - curW) / 2}px)`;
            }
        }
    }, []);

    return multipleLineMode ? (
        <MultipleLineContainer ref={multiRef} className={wrapperClassName}>
            <MultipleLineContent>
                {content}
            </MultipleLineContent>
        </MultipleLineContainer>
    ) : (<ToastContainer className={wrapperClassName}>{content}</ToastContainer>);
};

const getContainer = (id: string) => {
    const dom = document.getElementById(id) || document.createElement('div');
    dom.id = id;
    document.body.appendChild(dom);
    return dom;
};

export const showToast = (config: ToastConfig) => {
    const container = getContainer('toast-container');
    render(<Toast {...config} container={container} />, container);
};

const toastApi = {
    success: (content: React.ReactChild, duration?: number, callback?: () => void) => {
        const successContent = (
            <>
                <Icon
                    component={SuccessIcon}
                    style={{
                        marginRight: '0.8rem',
                        fontSize: '1.6rem',
                        verticalAlign: 'text-bottom'
                    }}
                />
                {content}
            </>
        );
        showToast({ content: successContent, duration, callback });
    }
};

export default toastApi;
